import React, { useCallback, useContext, useState, useMemo, useEffect } from 'react';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { SearchableDropdownField } from 'ui-components/components/dropdown/searchable';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { InlineMessage } from 'ui-components/components/inline-message';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message/types';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import style from './style.module.less';
import ModalPortal from '../../../../../../../components/modal-portal';
import { useModal } from '../../../../../../../hooks/use-modal';
import { EditLocationModal } from '../../../../../../../pages/settings/inventory-warehouses/tabs/manage-locations-tab/components/edit-location-modal';
import { moveInventoryForProduct } from '../../../../../../../store/inventory/inventory.api';
import { ProductDetailsContext } from '../../../../../context';
import { locationNameFromParts } from '../../../../../display-helpers';
import { messages as sharedMessages } from '../../shared-components/messages';
import { NameAndSku as NameAndSkuFields } from '../../shared-components/name-and-sku';
import { NotesInput } from '../../shared-components/notes-input';
import { QuantityInput } from '../../shared-components/quantity-input';

const ADD_NEW_LOCATION_VALUE = '__add_new__';

const getValidationState = ({ quantity, toLocationId, remainingStock, remainingAvailable, fromWarehouseId, fromLocationId, available }) => {
  const isNegativeOnHand = remainingStock < 0;
  const isNegativeAvailable = remainingAvailable < 0 && !isNegativeOnHand;
  const isQuantityExceedsAvailable = quantity > available && available >= 0;
  const isQuantityInvalid = quantity <= 0;
  const isLocationMissing = !toLocationId;
  const isFromWarehouseMissing = !fromWarehouseId;
  const isFromLocationMissing = !fromLocationId;
  const isFormValid =
    !isNegativeOnHand &&
    !isQuantityInvalid &&
    !isLocationMissing &&
    !isFromWarehouseMissing &&
    !isFromLocationMissing &&
    !isQuantityExceedsAvailable;

  return {
    isNegativeOnHand,
    isNegativeAvailable,
    isQuantityExceedsAvailable,
    isQuantityInvalid,
    isLocationMissing,
    isFromWarehouseMissing,
    isFromLocationMissing,
    isFormValid,
  };
};

/**
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {object[]} props.inventoryWarehouses
 * @param {object[]} props.inventoryWarehouseLocations
 * @param {string} props.productId
 * @param {string} props.productName
 * @param {string} props.productSku
 * @param {() => void} props.onReloadLocations
 * @param {string} [props.inventoryWarehouseId] - Optional: warehouse ID from URL when user clicked move button
 * @param {string} [props.inventoryLocationId] - Optional: pre-selected from location (locks the from location)
 * @param {string} [props.lotUuid]
 * @param {object[]} [props.productInventoryByLocation] - Optional: if provided, uses this instead of context
 * @param {string} [props.thumbnailUrl] - Optional: product image URL
 * @returns {JSX.Element}
 */
export const MoveInventoryModal = ({
  onClose,
  inventoryWarehouses,
  inventoryWarehouseLocations,
  productId,
  productName,
  productSku,
  onReloadLocations,
  inventoryWarehouseId,
  inventoryLocationId,
  lotUuid = '',
  productInventoryByLocation: productInventoryByLocationProp,
  thumbnailUrl: thumbnailUrlProp,
}) => {
  const { formatMessage } = useIntl();
  const { inventoryLocations: contextProductInventoryByLocation, imageUrl: contextImageUrl } = useContext(ProductDetailsContext) ?? {};
  const productInventoryByLocation = productInventoryByLocationProp ?? contextProductInventoryByLocation;
  const thumbnailUrl = thumbnailUrlProp ?? contextImageUrl;

  const { isOpen: isEditLocationModalOpen, open: openEditLocationModal, close: closeEditLocationModal } = useModal();

  const [localWarehouseLocations, setLocalWarehouseLocations] = useState(inventoryWarehouseLocations);

  const [fromWarehouseId, setFromWarehouseId] = useState(() => {
    if (inventoryWarehouseId) {
      return inventoryWarehouseId;
    }
    if (inventoryLocationId) {
      // Try inventoryWarehouseLocations first (all warehouse locations)
      const warehouseLocation = inventoryWarehouseLocations.find((loc) => loc.inventoryLocationId === inventoryLocationId);
      if (warehouseLocation?.inventoryWarehouseId) {
        return warehouseLocation.inventoryWarehouseId;
      }
      // Fallback to productInventoryByLocation (this product's inventory data)
      const productLocation = productInventoryByLocation?.find((loc) => loc.inventoryLocationId === inventoryLocationId);
      if (productLocation?.inventoryWarehouseId) {
        return productLocation.inventoryWarehouseId;
      }
    }
    return productInventoryByLocation?.[0]?.inventoryWarehouseId;
  });

  const [fromLocationId, setFromLocationId] = useState(() => {
    if (inventoryLocationId) {
      return inventoryLocationId;
    }
    const firstForWarehouse = productInventoryByLocation?.find((loc) => loc.inventoryWarehouseId === fromWarehouseId);
    return firstForWarehouse?.inventoryLocationId;
  });

  const [toWarehouseId, setToWarehouseId] = useState(fromWarehouseId);
  const [toLocationId, setToLocationId] = useState();
  const [quantity, setQuantity] = useState(0);
  const [notes, setNotes] = useState();
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const isFromLocationLocked = Boolean(inventoryLocationId);

  // Find stock data for selected from location
  const fromLocationData = useMemo(() => {
    return (
      productInventoryByLocation?.find((loc) => loc.inventoryLocationId === fromLocationId) ?? {
        stock: 0,
        allocated: 0,
        available: 0,
      }
    );
  }, [productInventoryByLocation, fromLocationId]);

  const onHand = fromLocationData.stock ?? 0;
  const allocated = fromLocationData.allocated ?? 0;
  const available = onHand - allocated;
  const remainingStock = onHand - quantity;
  const remainingAvailable = available - quantity;

  // Validation
  const {
    isNegativeOnHand,
    isQuantityExceedsAvailable,
    isQuantityInvalid,
    isLocationMissing,
    isFromWarehouseMissing,
    isFromLocationMissing,
    isFormValid,
  } = getValidationState({
    quantity,
    toLocationId,
    remainingStock,
    remainingAvailable,
    fromWarehouseId,
    fromLocationId,
    available,
  });

  // Dropdown options
  const warehouseOptions = useMemo(() => {
    return inventoryWarehouses.map((warehouse) => ({
      value: warehouse.inventoryWarehouseId,
      label: warehouse.name,
    }));
  }, [inventoryWarehouses]);

  // Filter "Move From" warehouses to only those with stock for this product
  const fromWarehouseOptions = useMemo(() => {
    const warehouseIdsWithStock = new Set(productInventoryByLocation?.map((loc) => loc.inventoryWarehouseId) ?? []);
    return inventoryWarehouses
      .filter((warehouse) => warehouseIdsWithStock.has(warehouse.inventoryWarehouseId))
      .map((warehouse) => ({
        value: warehouse.inventoryWarehouseId,
        label: warehouse.name,
      }));
  }, [inventoryWarehouses, productInventoryByLocation]);

  // Filter "Move From" locations to only those with stock for this product
  const fromLocationOptions = useMemo(() => {
    const locationsWithStock = productInventoryByLocation?.filter((loc) => loc.inventoryWarehouseId === fromWarehouseId) ?? [];
    const availableLabel = formatMessage(messages.moveInventoryAvailable);
    return locationsWithStock.map((loc) => ({
      value: loc.inventoryLocationId,
      label: `${loc.location}    (${availableLabel}: ${loc.available ?? 0})`,
    }));
  }, [productInventoryByLocation, fromWarehouseId, formatMessage]);

  const toLocationOptions = useMemo(() => {
    const toLocationsForWarehouse = localWarehouseLocations.filter(
      (loc) => loc.inventoryWarehouseId === toWarehouseId && loc.isActive !== false,
    );

    return [
      { value: ADD_NEW_LOCATION_VALUE, label: formatMessage(messages.moveInventoryAddNewLocation) },
      ...toLocationsForWarehouse.map((loc) => ({
        value: loc.inventoryLocationId,
        label: loc.name,
      })),
    ];
  }, [localWarehouseLocations, toWarehouseId, formatMessage]);

  // Single-option dropdown helpers
  const hasSingleFromWarehouse = fromWarehouseOptions.length === 1;
  const hasSingleFromLocation = fromLocationOptions.length === 1;

  // Auto-select single options
  useEffect(() => {
    if (hasSingleFromWarehouse && !fromWarehouseId) {
      setFromWarehouseId(fromWarehouseOptions[0].value);
    }
  }, [hasSingleFromWarehouse, fromWarehouseId, fromWarehouseOptions]);

  useEffect(() => {
    if (hasSingleFromLocation && !fromLocationId) {
      setFromLocationId(fromLocationOptions[0].value);
    }
  }, [hasSingleFromLocation, fromLocationId, fromLocationOptions]);

  // Handlers
  const handleFromWarehouseSelect = (value) => {
    setFromWarehouseId(value);
    setFromLocationId(null);
  };

  const handleFromLocationSelect = (value) => setFromLocationId(value);

  const handleToWarehouseSelect = (value) => {
    setToWarehouseId(value);
    setToLocationId(null);
  };

  const handleToLocationSelect = (value) => {
    if (value === ADD_NEW_LOCATION_VALUE) {
      openEditLocationModal();
    } else {
      setToLocationId(value);
    }
  };

  const handleMoveAll = useCallback(() => {
    setQuantity(available);
  }, [available]);

  const handleLocationCreated = useCallback(
    (newLocationData) => {
      let locationData;

      if (Array.isArray(newLocationData)) {
        const existingIds = new Set(localWarehouseLocations.map((loc) => loc.inventoryLocationId));
        locationData = newLocationData.find(
          (loc) => !existingIds.has(loc.inventoryLocationId) && loc.inventoryWarehouseId === toWarehouseId,
        );
        if (!locationData) {
          locationData = newLocationData[newLocationData.length - 1];
        }
      } else {
        locationData = newLocationData;
      }

      if (locationData?.inventoryLocationId) {
        const newLocation = {
          ...locationData,
          name: locationNameFromParts(locationData),
        };
        setLocalWarehouseLocations((prev) => [...prev, newLocation]);
        setToLocationId(locationData.inventoryLocationId);
      }
      closeEditLocationModal();
      onReloadLocations();
    },
    [onReloadLocations, localWarehouseLocations, toWarehouseId, closeEditLocationModal],
  );

  const onMoveInventory = useCallback(() => {
    setAttemptedSubmit(true);
    if (!isFormValid) {
      return;
    }
    moveInventoryForProduct({
      productId,
      inventoryLocationId: fromLocationId,
      moveToInventoryLocationId: toLocationId,
      quantity,
      description: notes,
      lotUuid,
    });
    onReloadLocations();
    onClose();
  }, [productId, fromLocationId, toLocationId, quantity, notes, onClose, onReloadLocations, lotUuid, isFormValid]);

  return (
    <>
      <Modal className={style['move-inventory-modal']} size='medium'>
        <ModalHeader closeModal={onClose}>
          <Text textType={TextType.H3} value={messages.moveInventoryModalTitle} />
        </ModalHeader>
        <ModalBody>
          <div className={style['move-inventory-wrapper']}>
            <NameAndSkuFields productName={productName} productSku={productSku} thumbnailUrl={thumbnailUrl} />

            <FlexRow className={style['side-by-side-container']}>
              {/* Move From */}
              <FlexColumn className={style['from-side']}>
                <Text className={style['to-from-header']} textType={TextType.H4} value={messages.moveInventoryFromHeader} />
                <SearchableDropdownField
                  id='from-warehouse'
                  isDisabled={isFromLocationLocked || hasSingleFromWarehouse}
                  label={sharedMessages.addToLocationWarehouse}
                  onItemSelected={handleFromWarehouseSelect}
                  options={fromWarehouseOptions}
                  selectedItemValue={fromWarehouseId ?? ''}
                />
                <SearchableDropdownField
                  id='from-location'
                  isDisabled={isFromLocationLocked || hasSingleFromLocation}
                  label={sharedMessages.addToLocationLocationLabel}
                  onItemSelected={handleFromLocationSelect}
                  options={fromLocationOptions}
                  selectedItemValue={fromLocationId ?? ''}
                />
                {attemptedSubmit && isFromWarehouseMissing ? (
                  <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                    {formatMessage(messages.moveInventoryErrorFromWarehouseRequired)}
                  </InlineMessage>
                ) : null}
                {attemptedSubmit && isFromLocationMissing && !isFromWarehouseMissing ? (
                  <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                    {formatMessage(messages.moveInventoryErrorFromLocationRequired)}
                  </InlineMessage>
                ) : null}
                {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role */}
                <div aria-label='Stock information' className={style['stock-info-panel']} role='region'>
                  <FlexRow className={style['stock-info-row']}>
                    <Text className={style['stock-info-label']} textType={TextType.Caption} value={messages.moveInventoryOnHand} />
                    <Text className={style['stock-info-value']} isBold textType={TextType.Caption} value={String(onHand)} />
                  </FlexRow>
                  <FlexRow className={style['stock-info-row']}>
                    <Text className={style['stock-info-label']} textType={TextType.Caption} value={messages.moveInventoryAllocated} />
                    <Text className={style['stock-info-value']} isBold textType={TextType.Caption} value={String(allocated)} />
                  </FlexRow>
                  <FlexRow className={style['stock-info-row']}>
                    <Text className={style['stock-info-label']} textType={TextType.Caption} value={messages.moveInventoryAvailable} />
                    <Text className={style['stock-info-value']} isBold textType={TextType.Caption} value={String(available)} />
                  </FlexRow>
                  <FlexRow className={style['stock-info-row']}>
                    <Text
                      className={style['stock-info-label']}
                      textType={TextType.Caption}
                      value={messages.moveInventoryRemainingAfterTransfer}
                    />
                    <Text className={style['stock-info-value']} isBold textType={TextType.Caption} value={String(remainingStock)} />
                  </FlexRow>
                </div>
              </FlexColumn>

              {/* Arrows */}
              <FlexColumn className={style['arrow-container']}>
                <FontAwesomeIcon aria-hidden='true' className={style['arrow-icon']} icon={faArrowRight} />
                <FontAwesomeIcon aria-hidden='true' className={style['arrow-icon']} icon={faArrowRight} />
              </FlexColumn>

              {/* Move To */}
              <FlexColumn className={style['to-side']}>
                <Text className={style['to-from-header']} textType={TextType.H4} value={messages.moveInventoryToHeader} />
                <SearchableDropdownField
                  id='to-warehouse'
                  label={sharedMessages.addToLocationWarehouse}
                  onItemSelected={handleToWarehouseSelect}
                  options={warehouseOptions}
                  selectedItemValue={toWarehouseId ?? ''}
                />
                <SearchableDropdownField
                  id='to-location'
                  label={sharedMessages.addToLocationLocationLabel}
                  onItemSelected={handleToLocationSelect}
                  options={toLocationOptions}
                  selectedItemValue={toLocationId ?? ''}
                />
                {attemptedSubmit && isLocationMissing ? (
                  <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                    {formatMessage(messages.moveInventoryErrorLocationRequired)}
                  </InlineMessage>
                ) : null}
                <div className={style['quantity-section']}>
                  <FlexRow className={style['quantity-with-button']} gap={Gap.Space8}>
                    <QuantityInput onChange={setQuantity} quantity={quantity} slimInput={false} />
                    <Button className={style['move-all-button']} onClick={handleMoveAll} theme='secondary'>
                      <Text textType={TextType.Caption} value={messages.moveInventoryMoveAll} />
                    </Button>
                  </FlexRow>
                  {attemptedSubmit && isQuantityInvalid ? (
                    <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                      {formatMessage(messages.moveInventoryErrorQuantityRequired)}
                    </InlineMessage>
                  ) : null}
                  {isNegativeOnHand ? (
                    <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                      {formatMessage(messages.moveInventoryErrorNegativeOnHand)}
                    </InlineMessage>
                  ) : null}
                  {isQuantityExceedsAvailable ? (
                    <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
                      {formatMessage(messages.moveInventoryErrorQuantityExceedsAvailable)}
                    </InlineMessage>
                  ) : null}
                </div>
                <NotesInput notes={notes} setNotes={setNotes} />
              </FlexColumn>
            </FlexRow>
          </div>
        </ModalBody>
        <ModalFooter>
          <FlexRow className={style['modal-buttons']}>
            <Button onClick={onClose} theme='tertiary'>
              <Text textType={TextType.Caption} value={messages.moveInventoryModalCancel} />
            </Button>
            <Button onClick={onMoveInventory} theme='primary'>
              <Text textType={TextType.Caption} value={messages.moveInventoryModalMoveStock} />
            </Button>
          </FlexRow>
        </ModalFooter>
      </Modal>

      {isEditLocationModalOpen ? (
        <ModalPortal onClose={closeEditLocationModal}>
          <EditLocationModal inventoryWarehouseId={toWarehouseId} onClose={handleLocationCreated} />
        </ModalPortal>
      ) : null}
    </>
  );
};
